/**
 * Memoize an async function. If the function has already been called with the
 * same arguments, return the same promise.
 *
 * The cache is stored in memory, with the key being generated by the `makeCacheKey` function
 * (which defaults to JSON.stringify). On first call, the cache stores the promise returned by the original function,
 * and subsequently returns the same promise if the function is called with the same arguments.
 *
 * WARNING: This function should only be used for functions that are idempotent and have no side effects,
 * which is less common for async functions. It was written for a get-only server action, and
 * should generally only be used for similar use cases.
 *
 * @param fn The async function to memoize.
 * @param makeCacheKey A function that takes in the arguments and returns a string key. Defaults to JSON.stringify.
 * @returns A memoized version of the function.
 */
const memoizeAsync = <TArgs extends any[], TReturn>(
	fn: (...args: TArgs) => Promise<TReturn>,
	makeCacheKey: (args: TArgs) => string = JSON.stringify
): ((...args: TArgs) => Promise<TReturn>) => {
	// cache holds promises, which may be pending or resolved
	const cache: Record<string, Promise<TReturn>> = {};
	return async (...args: TArgs) => {
		// Check if the cache is already holding a promise for these arguments
		const key = makeCacheKey(args);
		const cachedPromise = cache[key];
		if (cachedPromise) {
			return cachedPromise;
		}
		// If cache missed, call the original function and cache the returned promise
		const promise = fn(...args);
		cache[key] = promise;
		return promise;
	};
};

export default memoizeAsync;
